/**
 * EscapeCataloguePanel — `/admin → Escape Catalogue` tab.
 *
 * Founder ruling 18 Feb 2026 PM-12:
 *   "Surface Signature Escapes clearly in admin. The desk must be
 *    able to open `Escape Catalogue → Signature Escapes` and edit
 *    member_whisper, internal notes, images, publish state."
 *
 * Wave A scope (this panel):
 *   ▸ Signature Escapes  · FULLY EDITABLE (40 rows)
 *   ▸ Escape Requests    · read-only inbox of Zoho-routed requests
 *   ▸ Viator Products    · stub (Wave B · full coverage rebuild)
 *   ▸ Concierge® Picks   · stub (Wave B)
 *   ▸ Coverage Audit     · stub (Wave B · /api/admin/escape/coverage)
 *   ▸ Image Issues       · stub (Wave B)
 *   ▸ Broken Partner Links · stub (Wave B)
 *
 * The Signature edit drawer renders TWO panes:
 *   ▸ Member-facing (what members see)
 *   ▸ Desk-internal (never rendered to members; lives in the desk)
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const SECTIONS = [
    { id: "signature",     label: "Signature Escapes",       waveA: true  },
    { id: "requests",      label: "Escape Requests",          waveA: true  },
    { id: "viator",        label: "Viator Products",          waveA: false },
    { id: "picks",         label: "Concierge® Picks",         waveA: false },
    { id: "coverage",      label: "Coverage Audit",           waveA: false },
    { id: "images",        label: "Image Issues",             waveA: false },
    { id: "broken-urls",   label: "Broken Partner Links",     waveA: false },
];

const PUBLISH_FILTERS = [
    { id: "all",      label: "All" },
    { id: "live",     label: "Live" },
    { id: "draft",    label: "Drafts" },
    { id: "archived", label: "Archived" },
];

/** Returns `{ isSuperAdmin, email }`. Reads /auth/me once on mount. */
function useSuperAdmin() {
    const [state, setState] = useState({ isSuperAdmin: false, email: null, loaded: false });
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                if (mounted) setState({
                    isSuperAdmin: !!data?.is_super_admin,
                    email: data?.email || null,
                    loaded: true,
                });
            } catch {
                if (mounted) setState({ isSuperAdmin: false, email: null, loaded: true });
            }
        })();
        return () => { mounted = false; };
    }, []);
    return state;
}

export default function EscapeCataloguePanel() {
    const [section, setSection] = useState("signature");
    return (
        <div data-testid="admin-escape-catalogue-panel">
            <header className="mb-6">
                <h2 className="t-serif text-2xl text-[hsl(var(--navy))] mb-1">
                    Escape Catalogue
                </h2>
                <p className="text-sm text-[hsl(var(--muted))]">
                    The Concierge® door to tours, activities, attractions and Signature Escapes. Manage every layer that powers <code className="text-xs">/escape</code>.
                </p>
            </header>

            <nav
                role="tablist"
                aria-label="Escape catalogue sections"
                className="flex flex-wrap gap-x-6 gap-y-2 border-b border-[hsl(var(--brass))]/30 pb-3 mb-6"
            >
                {SECTIONS.map((s) => (
                    <button
                        key={s.id}
                        role="tab"
                        aria-selected={section === s.id}
                        data-testid={`escape-catalogue-tab-${s.id}`}
                        onClick={() => setSection(s.id)}
                        className={`text-[12px] tracking-[0.18em] uppercase transition-colors ${
                            section === s.id
                                ? "text-[hsl(var(--brass))]"
                                : "text-[hsl(var(--muted))] hover:text-[hsl(var(--navy))]"
                        }`}
                    >
                        {s.label}
                        {!s.waveA && (
                            <span className="ml-2 text-[9px] tracking-[0.12em] text-[hsl(var(--muted))]">
                                · Wave B
                            </span>
                        )}
                    </button>
                ))}
            </nav>

            {section === "signature"   && <SignatureSection />}
            {section === "requests"    && <RequestsSection />}
            {section === "viator"      && <WaveBStub label="Viator Products" hint="Coverage-wide product table + cache controls." />}
            {section === "picks"       && <WaveBStub label="Concierge® Picks" hint="Desk-curated Viator products elevated as Picks." />}
            {section === "coverage"    && <CoverageSection />}
            {section === "images"      && <WaveBStub label="Image Issues" hint="Cards / signature rows whose hero_image_url is 404 or empty." />}
            {section === "broken-urls" && <WaveBStub label="Broken Partner Links" hint="Products whose url_status is invalid / missing / 404." />}
        </div>
    );
}

/* ── Wave B stub · placeholder with intent ──────────────────── */
function WaveBStub({ label, hint }) {
    return (
        <div
            data-testid={`escape-catalogue-stub-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            className="rounded-xl border border-dashed border-[hsl(var(--brass))]/40 p-8 bg-[hsl(var(--paper))]/40 text-center"
        >
            <p className="text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brass))] mb-2">
                Coming · Wave B
            </p>
            <h3 className="t-serif text-xl text-[hsl(var(--navy))] mb-2">{label}</h3>
            <p className="text-sm text-[hsl(var(--muted))] max-w-md mx-auto leading-relaxed">{hint}</p>
        </div>
    );
}

/* ── Signature Escapes section · the live one ───────────────── */
function SignatureSection() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [filter, setFilter] = useState("all");
    const [editing, setEditing] = useState(null);
    const [auditing, setAuditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [importing, setImporting] = useState(false);
    const { isSuperAdmin } = useSuperAdmin();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const params = { limit: 500 };
            if (filter !== "all") params.publish_status = filter;
            const { data } = await api.get(`/admin/escape/signature`, { params });
            setRows(data.results || []);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail || e?.message));
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => {
        const c = { live: 0, draft: 0, archived: 0 };
        rows.forEach((r) => { c[r.publish_status] = (c[r.publish_status] || 0) + 1; });
        return c;
    }, [rows]);

    function startNewEscape() {
        // Open the same edit drawer in "create mode". The drawer
        // POSTs to /signature on first save and then switches into
        // edit mode with the returned id.
        setEditing({
            id: null,
            isNew: true,
            publish_status: "draft",
            human_confirmed: false,
            safety_reviewed: false,
        });
    }

    return (
        <section data-testid="escape-catalogue-signature-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex gap-2 flex-wrap" role="tablist" aria-label="Publish status filter">
                    {PUBLISH_FILTERS.map((f) => (
                        <button
                            key={f.id}
                            data-testid={`signature-filter-${f.id}`}
                            onClick={() => setFilter(f.id)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition ${
                                filter === f.id
                                    ? "bg-[hsl(var(--navy))] text-white border-[hsl(var(--navy))]"
                                    : "bg-transparent text-[hsl(var(--ink))] border-[hsl(var(--brass))]/40 hover:border-[hsl(var(--brass))]"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        data-testid="signature-new-escape-button"
                        onClick={startNewEscape}
                        className="text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--navy))] text-white hover:opacity-90 transition"
                    >+ New Escape</button>
                    <button
                        type="button"
                        data-testid="signature-import-workbook-button"
                        onClick={() => setImporting(true)}
                        className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--brass))] text-[hsl(var(--brass))] hover:bg-[hsl(var(--brass))]/10 transition"
                    >↑ Import Workbook</button>
                    <BulkRewriteButton onDone={load} />
                    <BackfillImagesButton onDone={load} />
                    <div
                        className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted))]"
                        data-testid="signature-counts"
                    >
                        {counts.live} live · {counts.draft} drafts · {counts.archived} archived
                    </div>
                </div>
            </div>

            {loading && <p className="text-sm text-[hsl(var(--muted))]">Loading…</p>}
            {err && (
                <p className="text-sm text-red-700" data-testid="signature-load-error">
                    {err}
                </p>
            )}

            {!loading && rows.length === 0 && (
                <p className="text-sm text-[hsl(var(--muted))]" data-testid="signature-empty">
                    No rows for this filter yet.
                </p>
            )}

            {!loading && rows.length > 0 && (
                <div className="overflow-x-auto" data-testid="signature-table">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--muted))] border-b border-[hsl(var(--brass))]/30">
                                <th className="py-2 pr-3 text-left">Title</th>
                                <th className="py-2 pr-3 text-left">Destination</th>
                                <th className="py-2 pr-3 text-left">Status</th>
                                <th className="py-2 pr-3 text-left">Image</th>
                                <th className="py-2 pr-3 text-left">Member whisper</th>
                                <th className="py-2 pr-3 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <SignatureRow
                                    key={r.id}
                                    row={r}
                                    onEdit={() => setEditing(r)}
                                    onAudit={() => setAuditing(r)}
                                    onDelete={() => setDeleting(r)}
                                    onChanged={load}
                                    isSuperAdmin={isSuperAdmin}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <SignatureEditDrawer
                    row={editing}
                    onClose={() => setEditing(null)}
                    onSaved={(savedRow) => {
                        // For new rows · keep the drawer open in edit mode
                        // so the desk can keep enriching fields (member
                        // whisper, image, gates) without re-opening.
                        if (editing.isNew && savedRow?.id) {
                            setEditing({ ...savedRow, isNew: false });
                        } else {
                            setEditing(null);
                        }
                        load();
                    }}
                />
            )}

            {auditing && (
                <SignatureAuditDrawer
                    row={auditing}
                    onClose={() => setAuditing(null)}
                />
            )}

            {deleting && (
                <HardDeleteModal
                    row={deleting}
                    onClose={() => setDeleting(null)}
                    onDeleted={() => { setDeleting(null); load(); }}
                />
            )}

            {importing && (
                <ImportWorkbookModal
                    onClose={() => setImporting(false)}
                    onImported={() => { setImporting(false); load(); }}
                />
            )}
        </section>
    );
}

/* ── Bulk Member-Rewrite Button · runs against all live rows
 *  missing member_whisper. Suggestions land in member_whisper_draft
 *  (NOT live) so the desk still reviews each row before publishing. */
function BulkRewriteButton({ onDone }) {
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    async function start() {
        if (!window.confirm(
            "Run AI member-rewrite across all live signatures missing a member whisper?\n\n"
            + "Suggestions land in `member_whisper_draft` — the desk reviews each row before promoting to live."
        )) return;
        setBusy(true);
        setErr(null);
        try {
            const { data } = await api.post(`/admin/escape/signature/bulk-rewrite`);
            setJob(data);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail || e?.message));
        } finally {
            setBusy(false);
        }
    }

    // Poll progress while running
    useEffect(() => {
        if (!job || job.status !== "running") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/admin/escape/signature/bulk-rewrite/${job.job_id}`);
                setJob(data);
                if (data.status === "done") {
                    clearInterval(t);
                    onDone?.();
                }
            } catch { /* ignore · keep polling */ }
        }, 3000);
        return () => clearInterval(t);
    }, [job?.job_id, job?.status, onDone]);

    return (
        <div className="flex items-center gap-2">
            <button
                type="button"
                data-testid="signature-bulk-rewrite-button"
                onClick={start}
                disabled={busy || (job && job.status === "running")}
                className="text-[11px] tracking-[0.1em] uppercase px-3 py-1.5 rounded-full border border-[hsl(var(--brass))]/60 text-[hsl(var(--brass))] hover:bg-[hsl(var(--brass))]/10 transition disabled:opacity-60"
            >
                {job && job.status === "running"
                    ? `Drafting ${job.done + job.errors}/${job.queued}…`
                    : "✨ Rewrite all"}
            </button>
            {job && job.status === "done" && (
                <span
                    className="text-[10px] uppercase tracking-[0.14em] text-emerald-700"
                    data-testid="signature-bulk-rewrite-done"
                >
                    {job.done} drafts · {job.errors} errors
                </span>
            )}
            {err && <span className="text-[11px] text-red-700">{err}</span>}
        </div>
    );
}

/* ── Backfill Viator Images · scans live rows · replaces broken /
 *  empty hero_image_url values with the first matching Viator
 *  destination photo. Never overwrites a working image. */
function BackfillImagesButton({ onDone }) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);

    async function go() {
        if (!window.confirm(
            "Scan all live signatures for broken / missing images and replace them with Viator destination photos?"
        )) return;
        setBusy(true);
        setErr(null);
        setResult(null);
        try {
            const { data } = await api.post(`/admin/escape/signature/backfill-viator-images?replace_broken=true`);
            setResult(data);
            onDone?.();
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail || e?.message));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="flex items-center gap-2">
            <button
                type="button"
                data-testid="signature-backfill-images-button"
                onClick={go}
                disabled={busy}
                className="text-[11px] tracking-[0.1em] uppercase px-3 py-1.5 rounded-full border border-[hsl(var(--brass))]/60 text-[hsl(var(--brass))] hover:bg-[hsl(var(--brass))]/10 transition disabled:opacity-60"
            >
                {busy ? "Backfilling…" : "🖼 Backfill images"}
            </button>
            {result && (
                <span
                    className="text-[10px] uppercase tracking-[0.14em] text-emerald-700"
                    data-testid="signature-backfill-images-done"
                >
                    {result.filled} filled · {result.already_ok} already OK
                </span>
            )}
            {err && <span className="text-[11px] text-red-700">{err}</span>}
        </div>
    );
}

/* ── Single row · table cell with quick actions ─────────────── */
function SignatureRow({ row, onEdit, onAudit, onDelete, onChanged, isSuperAdmin }) {
    const [busy, setBusy] = useState(false);
    const memberWhisperPreview =
        (row.member_whisper && row.member_whisper.trim()) ||
        (row.concierge_whisper && row.concierge_whisper.trim()) || "—";

    async function action(verb) {
        if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} "${row.title}"?`)) return;
        setBusy(true);
        try {
            if (verb === "publish") {
                await api.post(`/admin/escape/signature/${row.id}/publish`);
            } else if (verb === "archive") {
                await api.post(`/admin/escape/signature/${row.id}/archive`);
            } else if (verb === "unpublish") {
                await api.patch(`/admin/escape/signature/${row.id}`, { publish_status: "draft" });
            } else if (verb === "restore") {
                await api.post(`/admin/escape/signature/${row.id}/restore`);
            }
            onChanged?.();
        } catch (e) {
            window.alert(formatApiError(e?.response?.data?.detail || e?.message));
        } finally {
            setBusy(false);
        }
    }

    return (
        <tr
            data-testid={`signature-row-${row.id}`}
            className="border-b border-[hsl(var(--brass))]/15 hover:bg-[hsl(var(--paper))]/40"
        >
            <td className="py-3 pr-3">
                <div className="t-serif text-[15px] text-[hsl(var(--navy))]">{row.title || "(untitled)"}</div>
                {row.supplier_name && (
                    <div className="text-[11px] text-[hsl(var(--muted))] mt-0.5">{row.supplier_name}</div>
                )}
            </td>
            <td className="py-3 pr-3 text-[13px] text-[hsl(var(--ink))]">
                {[row.destination, row.country].filter(Boolean).join(" · ") || "—"}
            </td>
            <td className="py-3 pr-3">
                <StatusPill status={row.publish_status} />
            </td>
            <td className="py-3 pr-3">
                {row.hero_image_url ? (
                    <img
                        src={row.hero_image_url}
                        alt=""
                        className="w-14 h-10 object-cover rounded border border-[hsl(var(--brass))]/30"
                        data-testid={`signature-row-thumb-${row.id}`}
                    />
                ) : (
                    <span className="text-[11px] text-[hsl(var(--muted))]">— no image</span>
                )}
            </td>
            <td className="py-3 pr-3 max-w-md">
                <p
                    className="text-[12.5px] text-[hsl(var(--muted))] leading-snug line-clamp-2"
                    style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                >
                    {memberWhisperPreview}
                </p>
                {!row.member_whisper && row.concierge_whisper && (
                    <span
                        data-testid={`signature-row-needs-member-whisper-${row.id}`}
                        className="inline-block mt-1 text-[9px] tracking-[0.16em] uppercase text-amber-700"
                    >
                        Needs member rewrite
                    </span>
                )}
            </td>
            <td className="py-3 pr-3">
                <div className="flex gap-2 flex-wrap items-center">
                    <button
                        data-testid={`signature-row-edit-${row.id}`}
                        onClick={onEdit}
                        className="text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--navy))] text-white"
                        disabled={busy}
                    >Edit</button>
                    {row.publish_status !== "live" && row.publish_status !== "archived" && (
                        <button
                            data-testid={`signature-row-publish-${row.id}`}
                            onClick={() => action("publish")}
                            className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--brass))] text-[hsl(var(--brass))]"
                            disabled={busy}
                        >Publish</button>
                    )}
                    {row.publish_status === "live" && (
                        <button
                            data-testid={`signature-row-unpublish-${row.id}`}
                            onClick={() => action("unpublish")}
                            className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--muted))] text-[hsl(var(--muted))]"
                            disabled={busy}
                        >Unpublish</button>
                    )}
                    {row.publish_status !== "archived" && (
                        <button
                            data-testid={`signature-row-archive-${row.id}`}
                            onClick={() => action("archive")}
                            className="text-xs px-3 py-1.5 rounded-full border border-red-300 text-red-700"
                            disabled={busy}
                        >Archive</button>
                    )}
                    {row.publish_status === "archived" && (
                        <button
                            data-testid={`signature-row-restore-${row.id}`}
                            onClick={() => action("restore")}
                            className="text-xs px-3 py-1.5 rounded-full border border-emerald-400 text-emerald-700"
                            disabled={busy}
                        >Restore</button>
                    )}
                    <button
                        data-testid={`signature-row-audit-${row.id}`}
                        onClick={onAudit}
                        className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--brass))]/40 text-[hsl(var(--ink))]"
                        disabled={busy}
                        title="View audit trail"
                    >Audit</button>
                    {/* Super-Admin only · Permanently delete (danger zone) */}
                    {isSuperAdmin && (
                        <button
                            data-testid={`signature-row-hard-delete-${row.id}`}
                            onClick={onDelete}
                            className="text-xs px-3 py-1.5 rounded-full bg-red-700 text-white hover:bg-red-800 transition"
                            disabled={busy}
                            title="Super Admin only · permanent deletion"
                        >⚠ Permanently delete</button>
                    )}
                </div>
            </td>
        </tr>
    );
}

function StatusPill({ status }) {
    const map = {
        live:     "bg-emerald-100 text-emerald-800",
        draft:    "bg-amber-100 text-amber-800",
        archived: "bg-stone-200 text-stone-700",
    };
    return (
        <span
            data-testid={`status-pill-${status || "unknown"}`}
            className={`inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded ${map[status] || "bg-gray-200 text-gray-700"}`}
        >
            {status || "—"}
        </span>
    );
}

/* ── Edit drawer · member-facing + desk-internal panes ──────── */
function SignatureEditDrawer({ row, onClose, onSaved }) {
    const isNew = !!row.isNew;
    const [form, setForm] = useState({
        title: row.title || "",
        destination: row.destination || "",
        country: row.country || "",
        region: row.region || "",
        hero_image_url: row.hero_image_url || "",
        supplier_name: row.supplier_name || "",
        supplier_url: row.supplier_url || "",
        source_url: row.source_url || "",
        season_best_months: row.season_best_months || "",
        duration: row.duration || "",
        best_for: row.best_for || "",
        // Member-facing
        member_whisper: row.member_whisper || "",
        concierge_whisper: row.concierge_whisper || "",
        safety_notes: row.safety_notes || "",
        visa_or_permit_notes: row.visa_or_permit_notes || "",
        age_fitness_notes: row.age_fitness_notes || "",
        cancellation_notes: row.cancellation_notes || "",
        // Internal
        concierge_whisper_internal: row.concierge_whisper_internal || "",
        desk_notes: row.desk_notes || "",
        safety_notes_internal: row.safety_notes_internal || "",
        // Flags
        human_confirmed: !!row.human_confirmed,
        safety_reviewed: !!row.safety_reviewed,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function save() {
        setBusy(true);
        setErr(null);
        try {
            if (isNew) {
                // POST · backend requires title + destination
                if (!form.title?.trim() || !form.destination?.trim()) {
                    setErr("Title and Destination are required to create a new escape.");
                    setBusy(false);
                    return;
                }
                const { data } = await api.post(`/admin/escape/signature`, form);
                onSaved?.(data);
            } else {
                await api.patch(`/admin/escape/signature/${row.id}`, form);
                onSaved?.(row);
            }
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail || e?.message));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            data-testid="signature-edit-drawer"
            className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30"
            onClick={onClose}
        >
            <div
                className="bg-[hsl(var(--paper))] w-[min(720px,100vw)] h-full overflow-y-auto border-l border-[hsl(var(--brass))]/40"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-[hsl(var(--paper))] border-b border-[hsl(var(--brass))]/30 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <p className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--brass))]">
                            Signature Escape · {isNew ? "New (draft)" : "Edit"}
                        </p>
                        <h3 className="t-serif text-xl text-[hsl(var(--navy))]">
                            {form.title || (isNew ? "Untitled draft" : row.title)}
                        </h3>
                        {isNew && (
                            <p
                                data-testid="signature-edit-new-hint"
                                className="text-[11px] text-[hsl(var(--muted))] italic mt-1"
                            >
                                Lands as <strong>draft</strong> · <strong>human_confirmed=false</strong>.
                                Title + Destination required to create.
                            </p>
                        )}
                    </div>
                    <button
                        data-testid="signature-edit-close"
                        onClick={onClose}
                        className="text-2xl text-[hsl(var(--muted))]"
                        aria-label="Close"
                    >×</button>
                </div>

                {err && (
                    <div className="mx-6 mt-4 p-3 rounded bg-red-50 text-red-800 text-sm">
                        {err}
                    </div>
                )}

                <div className="px-6 py-6 space-y-8">
                    {/* Identity */}
                    <FormSection title="Identity">
                        <Field label="Title" value={form.title} onChange={(v) => setField("title", v)} testid="signature-edit-title" />
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Destination" value={form.destination} onChange={(v) => setField("destination", v)} testid="signature-edit-destination" />
                            <Field label="Country" value={form.country} onChange={(v) => setField("country", v)} testid="signature-edit-country" />
                        </div>
                        <Field label="Region" value={form.region} onChange={(v) => setField("region", v)} testid="signature-edit-region" />
                    </FormSection>

                    {/* Image */}
                    <FormSection title="Hero image">
                        <Field
                            label="Image URL"
                            value={form.hero_image_url}
                            onChange={(v) => setField("hero_image_url", v)}
                            testid="signature-edit-hero-image-url"
                            placeholder="https://images.unsplash.com/photo-…"
                        />
                        {form.hero_image_url && (
                            <img
                                src={form.hero_image_url}
                                alt=""
                                className="mt-2 w-full max-w-md rounded border border-[hsl(var(--brass))]/30"
                                data-testid="signature-edit-hero-image-preview"
                            />
                        )}
                        <button
                            data-testid="signature-edit-hero-image-clear"
                            onClick={() => setField("hero_image_url", "")}
                            className="text-xs text-red-700 mt-2"
                        >Remove image</button>
                    </FormSection>

                    {/* Member-facing copy */}
                    <FormSection
                        title="Member-facing"
                        hint="What members see on the card and drawer. Keep it warm, second-person, no desk instructions."
                    >
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--muted))]">
                                    Member whisper
                                </span>
                                <RewriteAssistButton
                                    rowId={row.id}
                                    disabled={isNew}
                                    onSuggestion={(text) => setField("member_whisper", text)}
                                />
                            </div>
                            <textarea
                                value={form.member_whisper || ""}
                                rows={4}
                                onChange={(e) => setField("member_whisper", e.target.value)}
                                data-testid="signature-edit-member-whisper"
                                placeholder="A specialist polar route for travellers who want…"
                                className="w-full px-3 py-2 rounded border border-[hsl(var(--brass))]/30 bg-white text-sm focus:border-[hsl(var(--brass))] outline-none"
                            />
                        </div>
                        <p className="text-[11px] text-[hsl(var(--muted))] italic mt-1">
                            If empty, the page falls back to a safe Concierge® line. Aim for ~2–3 sentences in member voice.
                        </p>
                        <TextArea
                            label="Public whisper (legacy field)"
                            rows={3}
                            value={form.concierge_whisper}
                            onChange={(v) => setField("concierge_whisper", v)}
                            testid="signature-edit-concierge-whisper"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Best for" value={form.best_for} onChange={(v) => setField("best_for", v)} testid="signature-edit-best-for" />
                            <Field label="Duration" value={form.duration} onChange={(v) => setField("duration", v)} testid="signature-edit-duration" />
                        </div>
                        <Field label="Season · best months" value={form.season_best_months} onChange={(v) => setField("season_best_months", v)} testid="signature-edit-season" />
                        <TextArea label="Safety notes" rows={2} value={form.safety_notes} onChange={(v) => setField("safety_notes", v)} testid="signature-edit-safety-notes" />
                        <TextArea label="Visa / permit notes" rows={2} value={form.visa_or_permit_notes} onChange={(v) => setField("visa_or_permit_notes", v)} testid="signature-edit-visa-notes" />
                        <TextArea label="Age / fitness notes" rows={2} value={form.age_fitness_notes} onChange={(v) => setField("age_fitness_notes", v)} testid="signature-edit-age-notes" />
                        <TextArea label="Cancellation notes" rows={2} value={form.cancellation_notes} onChange={(v) => setField("cancellation_notes", v)} testid="signature-edit-cancellation-notes" />
                    </FormSection>

                    {/* Desk-internal */}
                    <FormSection
                        title="Desk-internal"
                        hint="NEVER shown to members. Captures sourcing rationale, suitability flags, contacts. Internal only."
                        intent="internal"
                    >
                        <TextArea
                            label="Internal whisper (desk voice)"
                            rows={4}
                            value={form.concierge_whisper_internal}
                            onChange={(v) => setField("concierge_whisper_internal", v)}
                            testid="signature-edit-internal-whisper"
                            placeholder="For members who want Antarctica with expedition depth rather than cruise spectacle…"
                        />
                        <TextArea
                            label="Desk notes"
                            rows={3}
                            value={form.desk_notes}
                            onChange={(v) => setField("desk_notes", v)}
                            testid="signature-edit-desk-notes"
                            placeholder="Operator contact, lead-time, cabin classes, edge cases…"
                        />
                        <TextArea
                            label="Internal safety notes"
                            rows={3}
                            value={form.safety_notes_internal}
                            onChange={(v) => setField("safety_notes_internal", v)}
                            testid="signature-edit-internal-safety"
                            placeholder="Drake crossing tolerance, altitude, accessibility caveats…"
                        />
                    </FormSection>

                    {/* Supplier */}
                    <FormSection title="Supplier / source">
                        <Field label="Supplier name" value={form.supplier_name} onChange={(v) => setField("supplier_name", v)} testid="signature-edit-supplier-name" />
                        <Field label="Supplier URL" value={form.supplier_url} onChange={(v) => setField("supplier_url", v)} testid="signature-edit-supplier-url" />
                        <Field label="Source URL" value={form.source_url} onChange={(v) => setField("source_url", v)} testid="signature-edit-source-url" />
                    </FormSection>

                    {/* Gates */}
                    <FormSection title="Publish gates">
                        <CheckBox
                            label="Human confirmed (desk has reviewed the row)"
                            checked={form.human_confirmed}
                            onChange={(v) => setField("human_confirmed", v)}
                            testid="signature-edit-human-confirmed"
                        />
                        <CheckBox
                            label="Safety reviewed (route, season, suitability checked)"
                            checked={form.safety_reviewed}
                            onChange={(v) => setField("safety_reviewed", v)}
                            testid="signature-edit-safety-reviewed"
                        />
                    </FormSection>
                </div>

                <div className="sticky bottom-0 bg-[hsl(var(--paper))] border-t border-[hsl(var(--brass))]/30 px-6 py-4 flex justify-end gap-3">
                    <button
                        data-testid="signature-edit-cancel"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded border border-[hsl(var(--brass))]/40 text-[hsl(var(--ink))]"
                    >Cancel</button>
                    <button
                        data-testid="signature-edit-save"
                        onClick={save}
                        disabled={busy}
                        className="px-4 py-2 text-sm rounded bg-[hsl(var(--navy))] text-white disabled:opacity-60"
                    >{busy ? "Saving…" : (isNew ? "Create draft" : "Save changes")}</button>
                </div>
            </div>
        </div>
    );
}

/* ── Form helpers ─────────────────────────────────────────────── */
function FormSection({ title, hint, intent, children }) {
    return (
        <section>
            <div className={`mb-3 ${intent === "internal" ? "pl-3 border-l-2 border-amber-400" : ""}`}>
                <h4 className="t-serif text-lg text-[hsl(var(--navy))]">{title}</h4>
                {hint && <p className="text-[12px] text-[hsl(var(--muted))] mt-1 leading-relaxed">{hint}</p>}
            </div>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function Field({ label, value, onChange, testid, placeholder }) {
    return (
        <label className="block">
            <span className="block text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] mb-1">{label}</span>
            <input
                type="text"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full px-3 py-2 rounded border border-[hsl(var(--brass))]/30 bg-white text-sm focus:border-[hsl(var(--brass))] outline-none"
            />
        </label>
    );
}

function TextArea({ label, value, onChange, rows = 3, testid, placeholder }) {
    return (
        <label className="block">
            <span className="block text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] mb-1">{label}</span>
            <textarea
                value={value || ""}
                rows={rows}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full px-3 py-2 rounded border border-[hsl(var(--brass))]/30 bg-white text-sm focus:border-[hsl(var(--brass))] outline-none"
            />
        </label>
    );
}

function CheckBox({ label, checked, onChange, testid }) {
    return (
        <label className="flex items-center gap-2 cursor-pointer">
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                data-testid={testid}
            />
            <span className="text-sm text-[hsl(var(--ink))]">{label}</span>
        </label>
    );
}

/* ── AI Member-Rewrite assist · Wave B-1 18 Feb 2026 PM-14 ─────
 *  Calls /admin/escape/signature/{id}/suggest-member-rewrite. The
 *  endpoint returns a Claude-drafted member-voice paragraph built
 *  from the row's internal whisper / desk notes / legacy whisper.
 *  The desk reviews + edits + saves manually. */
function RewriteAssistButton({ rowId, onSuggestion, disabled }) {
    const [busy, setBusy] = useState(false);
    const [hint, setHint] = useState(null);

    async function go() {
        if (disabled || !rowId) return;
        setBusy(true);
        setHint(null);
        try {
            const { data } = await api.post(
                `/admin/escape/signature/${rowId}/suggest-member-rewrite`,
            );
            if (data?.skipped) {
                setHint(data?.reason || "No internal text to rewrite from.");
            } else if (data?.suggestion) {
                onSuggestion?.(data.suggestion);
                setHint(`Drafted via ${data.model}. Review + edit before saving.`);
            }
        } catch (e) {
            setHint(formatApiError(e?.response?.data?.detail || e?.message));
        } finally {
            setBusy(false);
        }
    }

    return (
        <span className="inline-flex items-center gap-2">
            {hint && (
                <span
                    className="text-[10px] tracking-[0.12em] uppercase text-[hsl(var(--muted))]"
                    data-testid={`signature-rewrite-hint-${rowId || "new"}`}
                >{hint}</span>
            )}
            <button
                type="button"
                data-testid={`signature-rewrite-assist-${rowId || "new"}`}
                onClick={go}
                disabled={busy || disabled || !rowId}
                title={disabled ? "Save the draft first · then suggest a rewrite" : ""}
                className="text-[11px] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full border border-[hsl(var(--brass))]/50 text-[hsl(var(--brass))] hover:bg-[hsl(var(--brass))]/10 transition disabled:opacity-40"
            >
                {busy ? "Drafting…" : "✨ Suggest member rewrite"}
            </button>
        </span>
    );
}

/* ── Coverage Audit · live dashboard for /escape health ───── */
function CoverageSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [runId, setRunId] = useState(0);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setErr(null);
        (async () => {
            try {
                const { data: d } = await api.get(`/admin/escape/coverage`);
                if (mounted) setData(d);
            } catch (e) {
                if (mounted) setErr(formatApiError(e?.response?.data?.detail || e?.message));
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [runId]);

    if (loading) return <p className="text-sm text-[hsl(var(--muted))]" data-testid="coverage-loading">Running audit…</p>;
    if (err) return <p className="text-sm text-red-700" data-testid="coverage-error">{err}</p>;
    if (!data) return null;

    const sig = data.signature || {};
    const api_health = data.viator_api || {};
    const probes = data.coverage_probes || [];

    return (
        <section data-testid="escape-coverage-audit" className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-2">
                <p className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted))]">
                    Last audited · {data.generated_at?.slice(0, 19)?.replace("T", " ")}
                </p>
                <button
                    data-testid="coverage-rerun"
                    onClick={() => setRunId((r) => r + 1)}
                    className="text-[11px] tracking-[0.14em] uppercase px-3 py-1.5 rounded-full border border-[hsl(var(--brass))]/40 text-[hsl(var(--brass))]"
                >Re-run audit</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Viator API */}
                <Card title="Viator API · live ping">
                    <Stat
                        label="Status"
                        value={api_health.ok ? "Healthy" : "Failing"}
                        tone={api_health.ok ? "good" : "bad"}
                        testid="coverage-api-status"
                    />
                    <Stat label="Latency" value={api_health.latency_ms ? `${api_health.latency_ms} ms` : "—"} />
                    <Stat label="Sample" value={`"${api_health.sample_query}" → ${api_health.sample_count} cards`} />
                    <Stat label="URLs valid / total" value={`${api_health.url_valid || 0} / ${api_health.sample_count || 0}`} />
                    <Stat label="Images present" value={`${api_health.images_present || 0} / ${api_health.sample_count || 0}`} />
                </Card>

                {/* Signature catalogue */}
                <Card title="Signature catalogue">
                    <Stat label="Total rows" value={sig.total} testid="coverage-sig-total" />
                    <Stat label="Live (published + confirmed)" value={sig.live} tone={sig.live > 0 ? "good" : "warn"} />
                    <Stat label="Drafts" value={sig.draft} />
                    <Stat label="Archived" value={sig.archived} />
                    <Stat
                        label="Missing image"
                        value={sig.missing_image}
                        tone={sig.missing_image > 0 ? "warn" : "good"}
                    />
                    <Stat
                        label="Missing member_whisper"
                        value={sig.missing_member_whisper}
                        tone={sig.missing_member_whisper > 0 ? "warn" : "good"}
                    />
                </Card>
            </div>

            {/* Destination probes */}
            <Card title={`Destination probes · ${data.coverage_probe_pass}/${data.coverage_probe_total} cities passed`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="coverage-probes">
                    {probes.map((p) => (
                        <div
                            key={p.query}
                            data-testid={`coverage-probe-${p.query.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                            className={`p-3 rounded border ${p.ok && (p.count || 0) > 0 ? "border-emerald-300 bg-emerald-50/40" : "border-red-300 bg-red-50/30"}`}
                        >
                            <div className="text-[13px] font-medium text-[hsl(var(--ink))]">{p.query}</div>
                            <div className="text-[11px] text-[hsl(var(--muted))]">
                                {p.ok ? `${p.count} cards` : `error · ${p.error || "—"}`}
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card title="Click-intent telemetry">
                <Stat label="Outbound clicks · last 7 days" value={data.click_intents_7d ?? "—"} />
                <Stat label="Cached products (lc_escape_products)" value={data.cache?.lc_escape_products ?? "—"} />
            </Card>
        </section>
    );
}

function Card({ title, children }) {
    return (
        <div className="rounded-xl border border-[hsl(var(--brass))]/30 bg-[hsl(var(--paper))]/60 p-4">
            <h4 className="t-serif text-lg text-[hsl(var(--navy))] mb-3">{title}</h4>
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}

function Stat({ label, value, tone = "neutral", testid }) {
    const toneMap = {
        good: "text-emerald-700",
        warn: "text-amber-700",
        bad: "text-red-700",
        neutral: "text-[hsl(var(--ink))]",
    };
    return (
        <div className="flex justify-between items-baseline text-[13px]" data-testid={testid}>
            <span className="text-[hsl(var(--muted))]">{label}</span>
            <span className={`font-medium ${toneMap[tone]}`}>{value ?? "—"}</span>
        </div>
    );
}

/* ── Escape Requests · read-only Zoho inbox ─────────────────── */
function RequestsSection() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get(`/admin/escape/requests`, { params: { limit: 100 } });
                if (mounted) setRows(data.results || []);
            } catch (e) {
                if (mounted) setErr(formatApiError(e?.response?.data?.detail || e?.message));
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    if (loading) return <p className="text-sm text-[hsl(var(--muted))]">Loading requests…</p>;
    if (err) {
        // Endpoint may not exist yet — show a Wave B style stub instead of erroring.
        return (
            <WaveBStub
                label="Escape Requests"
                hint="Member requests routed to Zoho Desk. The live inbox surface lands in Wave B; for now monitor at desk.247concierge.com."
            />
        );
    }
    if (rows.length === 0) {
        return <p className="text-sm text-[hsl(var(--muted))]" data-testid="escape-requests-empty">No active requests.</p>;
    }
    return (
        <div data-testid="escape-requests-table">
            {/* Wave B will flesh this out; basic table for now */}
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--muted))] border-b border-[hsl(var(--brass))]/30">
                        <th className="py-2 text-left">When</th>
                        <th className="py-2 text-left">Member</th>
                        <th className="py-2 text-left">Title</th>
                        <th className="py-2 text-left">Destination</th>
                        <th className="py-2 text-left">Rail</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-[hsl(var(--brass))]/15">
                            <td className="py-2 text-[12px] text-[hsl(var(--muted))]">{r.created_at?.slice(0, 16)}</td>
                            <td className="py-2 text-[13px]">{r.member_email || r.member_name || "—"}</td>
                            <td className="py-2 text-[13px]">{r.title || "—"}</td>
                            <td className="py-2 text-[13px]">{[r.destination, r.country].filter(Boolean).join(" · ") || "—"}</td>
                            <td className="py-2 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--brass))]">{r.rail || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────
 * Audit trail · per-row mini drawer
 * Calls GET /admin/escape/signature/{id}/audit?limit=20.
 * Renders a chronological timeline of every state change.
 * ───────────────────────────────────────────────────────────── */
function SignatureAuditDrawer({ row, onClose }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get(
                    `/admin/escape/signature/${row.id}/audit`,
                    { params: { limit: 20 } },
                );
                if (mounted) setEvents(data.results || []);
            } catch (e) {
                if (mounted) setErr(formatApiError(e?.response?.data?.detail || e?.message));
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [row.id]);

    return (
        <div
            data-testid="signature-audit-drawer"
            className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30"
            onClick={onClose}
        >
            <div
                className="bg-[hsl(var(--paper))] w-[min(560px,100vw)] h-full overflow-y-auto border-l border-[hsl(var(--brass))]/40"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-[hsl(var(--paper))] border-b border-[hsl(var(--brass))]/30 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <p className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--brass))]">
                            Audit trail · last 20 events
                        </p>
                        <h3 className="t-serif text-lg text-[hsl(var(--navy))]">{row.title}</h3>
                    </div>
                    <button
                        data-testid="signature-audit-close"
                        onClick={onClose}
                        className="text-2xl text-[hsl(var(--muted))]"
                        aria-label="Close"
                    >×</button>
                </div>

                <div className="px-6 py-6">
                    {loading && (
                        <p className="text-sm text-[hsl(var(--muted))]" data-testid="signature-audit-loading">
                            Loading audit events…
                        </p>
                    )}
                    {err && (
                        <p className="text-sm text-red-700" data-testid="signature-audit-error">{err}</p>
                    )}
                    {!loading && !err && events.length === 0 && (
                        <p className="text-sm text-[hsl(var(--muted))]" data-testid="signature-audit-empty">
                            No audit events recorded.
                        </p>
                    )}
                    {!loading && events.length > 0 && (
                        <ol
                            className="relative border-l border-[hsl(var(--brass))]/40 pl-5 space-y-4"
                            data-testid="signature-audit-events"
                        >
                            {events.map((e, idx) => (
                                <AuditEvent key={idx} ev={e} />
                            ))}
                        </ol>
                    )}
                </div>
            </div>
        </div>
    );
}

function AuditEvent({ ev }) {
    const eventToneMap = {
        created: "emerald",
        patched: "navy",
        published: "emerald",
        unpublished: "amber",
        archived: "stone",
        restored: "emerald",
        imported: "navy",
        deleted: "red",
        delete_blocked_by_activity: "amber",
        member_rewrite_suggested: "brass",
        member_rewrite_accepted: "emerald",
        image_uploaded: "navy",
    };
    const tone = eventToneMap[ev.event] || "stone";
    const ts = ev.ts ? new Date(ev.ts).toLocaleString() : "—";
    return (
        <li
            data-testid={`signature-audit-event-${ev.event}`}
            className="relative"
        >
            <span
                className={`absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-${tone}-500 ring-2 ring-[hsl(var(--paper))]`}
                aria-hidden="true"
            />
            <div className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--muted))]">{ts}</div>
            <div className="text-[14px] t-serif text-[hsl(var(--navy))]">{ev.event.replace(/_/g, " ")}</div>
            <div className="text-[12px] text-[hsl(var(--muted))]">{ev.actor || "—"}</div>
            {ev.payload && Object.keys(ev.payload).length > 0 && (
                <details className="text-[11px] text-[hsl(var(--muted))] mt-1">
                    <summary className="cursor-pointer">payload</summary>
                    <pre className="mt-1 p-2 rounded bg-[hsl(var(--paper))] border border-[hsl(var(--brass))]/20 text-[10px] whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                        {JSON.stringify(ev.payload, null, 2)}
                    </pre>
                </details>
            )}
        </li>
    );
}

/* ─────────────────────────────────────────────────────────────
 * Hard-delete modal · Super-Admin only · typed confirmation.
 *
 * The button that opens this modal is already gated by isSuperAdmin
 * on the row, but the backend re-enforces the role on the request
 * (defence-in-depth) and requires `confirmation === "DELETE"`.
 * ───────────────────────────────────────────────────────────── */
function HardDeleteModal({ row, onClose, onDeleted }) {
    const [typed, setTyped] = useState("");
    const [reason, setReason] = useState("");
    const [override, setOverride] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [activityTraces, setActivityTraces] = useState(null);

    async function go() {
        if (typed.trim() !== "DELETE") return;
        setBusy(true);
        setErr(null);
        setActivityTraces(null);
        try {
            await api.delete(`/admin/escape/signature/${row.id}`, {
                data: {
                    confirmation: "DELETE",
                    reason: reason.trim() || null,
                    override_activity_block: override,
                },
            });
            onDeleted?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "activity_guard") {
                setErr(detail.message);
                setActivityTraces(detail.traces || []);
            } else {
                setErr(formatApiError(detail || e?.message));
            }
        } finally {
            setBusy(false);
        }
    }

    const canDelete = typed.trim() === "DELETE" && !busy;

    return (
        <div
            data-testid="signature-hard-delete-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={onClose}
        >
            <div
                className="bg-[hsl(var(--paper))] w-[min(560px,92vw)] rounded-xl border-2 border-red-700 p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-[10px] tracking-[0.22em] uppercase text-red-700 font-semibold">
                            ⚠ Danger zone · Super Admin only
                        </p>
                        <h3 className="t-serif text-xl text-[hsl(var(--navy))] mt-1">
                            Permanently delete this escape?
                        </h3>
                    </div>
                    <button
                        data-testid="signature-hard-delete-close"
                        onClick={onClose}
                        className="text-2xl text-[hsl(var(--muted))]"
                        aria-label="Close"
                    >×</button>
                </div>

                <div className="space-y-3 mb-4">
                    <p className="text-sm text-[hsl(var(--ink))]">
                        You are about to <strong>permanently delete</strong> the Signature Escape
                        <em className="text-[hsl(var(--navy))]"> &ldquo;{row.title}&rdquo;</em>.
                        This action is <strong>irreversible</strong>.
                    </p>
                    <p className="text-[12px] text-[hsl(var(--muted))]">
                        For routine takedowns, use <strong>Archive</strong> or <strong>Unpublish</strong> instead.
                        Hard-delete erases the row but the audit trail of this deletion survives.
                    </p>
                </div>

                {err && (
                    <div
                        data-testid="signature-hard-delete-error"
                        className="mb-3 p-3 rounded bg-red-50 border border-red-300 text-red-800 text-sm"
                    >
                        {err}
                        {activityTraces && activityTraces.length > 0 && (
                            <div className="mt-2">
                                <p className="text-[11px] font-semibold mb-1">Activity traces blocking delete:</p>
                                <ul className="text-[11px] list-disc pl-4 space-y-0.5">
                                    {activityTraces.map((t, i) => (
                                        <li key={i}>
                                            {t.event}
                                            {t.ts ? ` · ${new Date(t.ts).toLocaleString()}` : ""}
                                            {t.actor ? ` · ${t.actor}` : ""}
                                            {t.count != null ? ` · ${t.count} hits` : ""}
                                        </li>
                                    ))}
                                </ul>
                                <label className="flex items-center gap-2 mt-3 text-[12px] text-[hsl(var(--ink))]">
                                    <input
                                        type="checkbox"
                                        checked={override}
                                        data-testid="signature-hard-delete-override"
                                        onChange={(e) => setOverride(e.target.checked)}
                                    />
                                    <span>Override activity guard (Super Admin only)</span>
                                </label>
                            </div>
                        )}
                    </div>
                )}

                <label className="block mb-3">
                    <span className="block text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] mb-1">
                        Reason (audit log · optional)
                    </span>
                    <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="signature-hard-delete-reason"
                        placeholder="e.g. duplicate row · founder direction · GDPR request"
                        className="w-full px-3 py-2 rounded border border-[hsl(var(--brass))]/30 bg-white text-sm"
                    />
                </label>

                <label className="block mb-4">
                    <span className="block text-[11px] tracking-[0.14em] uppercase text-red-700 font-semibold mb-1">
                        Type <code className="bg-red-50 px-1 py-0.5 rounded">DELETE</code> to confirm
                    </span>
                    <input
                        type="text"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        data-testid="signature-hard-delete-typed"
                        autoComplete="off"
                        spellCheck={false}
                        className="w-full px-3 py-2 rounded border-2 border-red-300 bg-white text-sm font-mono focus:border-red-700 outline-none"
                    />
                </label>

                <div className="flex justify-end gap-3">
                    <button
                        data-testid="signature-hard-delete-cancel"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded border border-[hsl(var(--brass))]/40 text-[hsl(var(--ink))]"
                    >Cancel</button>
                    <button
                        data-testid="signature-hard-delete-confirm"
                        onClick={go}
                        disabled={!canDelete}
                        className="px-4 py-2 text-sm rounded bg-red-700 text-white hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                        {busy ? "Deleting…" : "Permanently delete"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────
 * Import Workbook modal · .xlsx upload to /signature/import.
 * Surfaces inserted / skipped / batch_id / per-row errors.
 * Imported rows ALWAYS land as draft + human_confirmed=false.
 * ───────────────────────────────────────────────────────────── */
function ImportWorkbookModal({ onClose, onImported }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);

    async function go() {
        const f = fileRef.current?.files?.[0];
        if (!f) {
            setErr("Choose a .xlsx workbook to upload.");
            return;
        }
        setBusy(true);
        setErr(null);
        setResult(null);
        const fd = new FormData();
        fd.append("file", f);
        try {
            const { data } = await api.post(
                `/admin/escape/signature/import`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setResult(data);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail || e?.message));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            data-testid="signature-import-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={onClose}
        >
            <div
                className="bg-[hsl(var(--paper))] w-[min(620px,92vw)] rounded-xl border border-[hsl(var(--brass))]/40 p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--brass))]">
                            Import Workbook
                        </p>
                        <h3 className="t-serif text-xl text-[hsl(var(--navy))] mt-1">
                            Bulk-import Signature Escapes
                        </h3>
                        <p className="text-[12px] text-[hsl(var(--muted))] mt-1">
                            Upload an .xlsx workbook. Every row lands as <strong>draft</strong> · <strong>human_confirmed=false</strong>.
                            Publish gate runs row-by-row when the desk approves.
                        </p>
                    </div>
                    <button
                        data-testid="signature-import-close"
                        onClick={onClose}
                        className="text-2xl text-[hsl(var(--muted))]"
                        aria-label="Close"
                    >×</button>
                </div>

                {!result && (
                    <div className="space-y-3">
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".xlsx,.xlsm"
                            data-testid="signature-import-file"
                            className="block w-full text-sm border border-[hsl(var(--brass))]/30 rounded p-2 bg-white"
                        />
                        {err && (
                            <p
                                data-testid="signature-import-error"
                                className="text-sm text-red-700"
                            >{err}</p>
                        )}
                        <div className="flex justify-end gap-3 mt-2">
                            <button
                                data-testid="signature-import-cancel"
                                onClick={onClose}
                                className="px-4 py-2 text-sm rounded border border-[hsl(var(--brass))]/40"
                            >Cancel</button>
                            <button
                                data-testid="signature-import-go"
                                onClick={go}
                                disabled={busy}
                                className="px-4 py-2 text-sm rounded bg-[hsl(var(--navy))] text-white disabled:opacity-60"
                            >{busy ? "Importing…" : "Import"}</button>
                        </div>
                    </div>
                )}

                {result && (
                    <div data-testid="signature-import-result" className="space-y-3">
                        <div className="rounded border border-emerald-300 bg-emerald-50/60 p-3 text-sm">
                            <p className="font-medium text-emerald-800">Import complete</p>
                            <div className="grid grid-cols-3 gap-3 mt-2 text-center">
                                <div>
                                    <div className="text-2xl t-serif text-emerald-700" data-testid="signature-import-inserted">{result.inserted ?? 0}</div>
                                    <div className="text-[10px] tracking-[0.14em] uppercase text-[hsl(var(--muted))]">inserted</div>
                                </div>
                                <div>
                                    <div className="text-2xl t-serif text-amber-700" data-testid="signature-import-skipped">{result.skipped ?? 0}</div>
                                    <div className="text-[10px] tracking-[0.14em] uppercase text-[hsl(var(--muted))]">skipped</div>
                                </div>
                                <div>
                                    <div className="text-2xl t-serif text-red-700" data-testid="signature-import-errors">{(result.errors || []).length}</div>
                                    <div className="text-[10px] tracking-[0.14em] uppercase text-[hsl(var(--muted))]">errors</div>
                                </div>
                            </div>
                            <p className="text-[11px] text-[hsl(var(--muted))] mt-3 font-mono break-all" data-testid="signature-import-batch-id">
                                batch_id · {result.batch_id || "—"}
                            </p>
                        </div>

                        {(result.errors || []).length > 0 && (
                            <details open>
                                <summary className="text-sm cursor-pointer text-red-700">
                                    {result.errors.length} row error(s) — expand
                                </summary>
                                <ul
                                    data-testid="signature-import-error-list"
                                    className="mt-2 max-h-48 overflow-y-auto text-[12px] space-y-1 border border-red-200 rounded p-2 bg-red-50/40"
                                >
                                    {result.errors.map((e, i) => (
                                        <li key={i}>
                                            <code className="text-red-700">row {e.row_no || "?"}</code> · {e.reason}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}

                        <div className="flex justify-end gap-3">
                            <button
                                data-testid="signature-import-done"
                                onClick={onImported}
                                className="px-4 py-2 text-sm rounded bg-[hsl(var(--navy))] text-white"
                            >Refresh table</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
